import { useQuery } from '@tanstack/react-query';
import { useEffect } from 'react';

import { BasicCompanyInformation } from '../../../../../typings/api/skymap/rest/v0/company';
import { SkyMapAxiosServiceFactory } from '../../../services/axios/skymap-axios-service-factory';
import { isDefined } from '../../../utils/variables';
import { useErrorHandling } from '../../hooks/use-error-handling';
import { useOptionalUserState } from '../../state/user-state/user-state';

function query(): Promise<BasicCompanyInformation[]> {
  return SkyMapAxiosServiceFactory.instance.createCompanyServiceV0().getCompanies();
}

/**
 * Fetches a list of basic project information for listing the projects in the project list.
 */
export function useGetCompaniesQuery(options?: { enabled?: boolean }) {
  const { handleError, hasError, clearErrors, buildErrorList } = useErrorHandling();
  const userState = useOptionalUserState();
  const queryId = userState?.user?.id ?? '';
  const enabled = options?.enabled ?? true;

  const { data, refetch, error, isLoading } = useQuery({
    queryKey: ['getCompanies', queryId],
    queryFn: query,
    enabled,
    networkMode: 'always',
    retry: false,
    refetchInterval: false,
    refetchOnMount: false,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
  });

  // Define effect on error outside of mutation to avoid infinite render loop.
  useEffect(() => {
    if (isDefined(error)) {
      handleError(error, { retryCallback: refetch });
    } else {
      clearErrors();
    }
  }, [handleError, clearErrors, error, refetch]);

  return {
    companies: data,
    buildErrorList,
    hasError,
    isLoading,
  };
}
